import React, { useMemo, useState, useCallback, useEffect } from 'react';
import styles from './card.module.scss';
import './customStyle.css';
import { Program } from '../../../types';
import { useHistory, useLocation } from 'react-router-dom';
import { getMediaAbsolutePath } from '../../../services/File';
import { AusCourseContent, CourseContentType, Unit, useGetAusCourseContentsLazyQuery, useGetCourseContentsLazyQuery } from '@api/graphql-codegen'
import UnitCard from '../unitsView/card/Card';
import { VideoPlayer } from '@components';
import UnitProgressModal from '@components/unit-progress-modal/UnitProgressModal';
import { useAnalytics, Events, useQueryParams, useUser } from '@hooks';
import BookMarkIcon from 'src/assets/icons/bookmark.svg?react';
import CompleteIcon from 'src/assets/icons/completed_check.svg?react';
import { Collapse } from '@src/utils/componentCasts';
import { countryOptions } from '@src/constants';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import Spinner from '@src/components/spinner/Spinner';
import CoverContent from '@src/components/CoverContent/CoverContent';
import LessonProgressModal from '@src/components/lesson-progress-modal/LessonProgressModal';
import { logger } from '../../../utils/logger';

type Props = {
  program: Program;
  showEdit?: boolean;
  isParentApp?: boolean;
  selectedId?: string | null;
  selected?: boolean;
  handleSeleted?: (program: Program) => void;
  handleNoneSelect?: () => void;
  specialCourseId?: string;
  handleCardHovered?: (unitId: string, hovered: boolean) => void;
  handleSelectedCenterCard?: (centerCard: boolean) => void
  cardHovered?: boolean;
  hoveredUnitId?: string;
  refresh?: () => void;
};

function Card(props: Props) {
  const {
    program,
    selectedId,
    selected,
    handleSeleted,
    handleNoneSelect,
    isParentApp,
    specialCourseId,
    cardHovered,
    hoveredUnitId,
    handleCardHovered,
    handleSelectedCenterCard,
    refresh
  } = props;

  const history = useHistory();
  const {pathname} = useLocation();
  const params = useQueryParams();
  const unitId = params.get('uid');
  const [isHovered, setIsHovered] = useState(false);
  const [showUnit, setShowUnit] = useState(false);
  const [showLesson, setShowLesson] = useState(false);
  const [parentSkill, setParentSkill] = useState<string | undefined>(undefined);
  const [selectedUnit, setselectedUnit] = useState<Unit>();
  const [showContentId, setShowContentId] = useState<string | undefined>(undefined);
  const { id, name, image, progress, country } = program;
  const { trackEvent } = useAnalytics();
  const [refreshing, setRefreshing] = useState(false)
  const [user] = useUser();

  const [getAusCourseContents, {data: ausProgramData, loading}] = useGetAusCourseContentsLazyQuery({variables: {input: {id}}, fetchPolicy: 'network-only'})
  const [getCourseContents, {data: programData, loading: loading1}] = useGetCourseContentsLazyQuery({variables: {input: {id}}, fetchPolicy: 'network-only'})

  const isAussie = useMemo(() => {
    return country === countryOptions[1].label
  }, [country]);

  const title = useMemo(() => {
    let title = name;
    
    let nameList = name.trim().split(' ');
    if (nameList[nameList.length - 1].toLowerCase() === 'program' || nameList[nameList.length - 1].toLowerCase() === 'started') {
      title = name.replace(nameList[nameList.length - 1], '').trim()
    } else if (name.toLowerCase().includes('coping')) {
      title = "Coping"
    } else if (name.toLowerCase().includes('parent resources')) {
      title = "Parent"
    } else if (name.toLowerCase().includes('caregiver resources')) {
      title = "Caregiver"
    }
    return title
  }, [name])

  const subtitle = useMemo(() => {
    let title = name;
    let nameList = name.trim().split(' ');
    if (nameList[nameList.length - 1].toLowerCase() === 'program' || nameList[nameList.length - 1].toLowerCase() === 'started' ) {
      title = nameList[nameList.length - 1]
    } else if (name.toLowerCase().includes('parent resources')) {
      title = name.replace('Parent', '').replace('-', '').trim()
    } else if (name.toLowerCase().includes('caregiver resources')) {
      title = name.replace('Caregiver', '').replace('-', '').trim()
    } else if (name.toLowerCase().includes('coping')) {
      title = "with COVID"
    } else {
      title = ""
    }
    return title
  }, [name])

  const handleEditUnit = (unit: Unit) => {
    history.push(`/super/units/edit/${unit.id}`);
  };

  const coreUnits = useMemo(() => [...programData?.getCourseContents?.primary || []], [programData?.getCourseContents?.primary]);
  const additionUnits = useMemo(() => [...programData?.getCourseContents?.additional || []], [programData?.getCourseContents?.additional]);
  const ausCourseContents = useMemo(() => [...ausProgramData?.getAUSCourseContents || []], [ausProgramData?.getAUSCourseContents])
  const isSelected = useMemo(() => {
    return (selected && selectedId === program.id) ? true : false;
  }, [selected, selectedId, program.id]);

  useEffect(() => {
    if (isAussie && isSelected) {
      getAusCourseContents()
    }
  }, [isAussie, isSelected, getAusCourseContents])

  useEffect(() => {
    if (!isAussie && isSelected) {
      getCourseContents()
    }
  }, [isAussie, isSelected, getCourseContents])

  const handleOpenUnit = useCallback((unit: Unit) => {
    if (unit) {
      setselectedUnit(unit);
      setShowUnit(true);
      const unitName = unit.name.replace(' ', '+')
      history.replace(`${pathname}?uid=${unit?.id}&${unitName}`);
      trackEvent(Events.actions.unitOpen, {
        course_name: name,
        unit_name: unit.name,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    }
    if (handleCardHovered) {
      handleCardHovered("", false)
    }
  }, [history, pathname, name, user, trackEvent, handleCardHovered]);

  const handleOpenContent = useCallback((content: AusCourseContent) => {
    if (content.type === CourseContentType.UNIT) {
      setShowUnit(true);
      setShowLesson(false);
    } else if (content.type === CourseContentType.LESSON) {
      setShowLesson(true);
      setShowUnit(false);
    }
    setShowContentId(content.id);
  }, [])

  useEffect(() => {
    if (specialCourseId && isParentApp) {
      const additionalUnit = additionUnits.find(unitData => {
        if (unitData.courseId) {
          try {
            const courseIds = JSON.parse(unitData.courseId);
            return courseIds.includes(specialCourseId);
          } catch {
            return false;
          }
        }
        return false;
      });

      if (additionalUnit) {
        setParentSkill(additionalUnit.skill);
        handleOpenUnit(additionalUnit)
      }
    }
  }, [additionUnits, handleOpenUnit, isParentApp, specialCourseId]);

  useEffect(() => {
    if (!unitId || selectedUnit?.id) return;
    setShowUnit(true)
    const sharedCoreUnit = coreUnits.find(unit => unit.id === unitId)
    const sharedAdditionUnit = additionUnits.find(unit => unit.id === unitId)
    if (sharedCoreUnit) {
      setselectedUnit(sharedCoreUnit);
    } else if (sharedAdditionUnit) {
      setselectedUnit(sharedAdditionUnit);
    }
  }, [unitId, selectedUnit?.id, coreUnits, additionUnits])

  const cpres = useMemo(() => {
    const classPresentation = [...coreUnits];
    
    return classPresentation;
  }, [coreUnits]);

  const hoverTitleViewStyle = useMemo(() => {
    const classes = [''];
    if (isHovered) {
      classes.push(styles.hoverTitleView);
      classes.push(styles.fadeIn);
    } else {
      classes.push(styles.hoverTitleViewHidden);
      classes.push(styles.fadeOut);
    }
    return classes.join(' ');
  }, [isHovered])

  const titleViewStyle = useMemo(() => {
    const classes = [styles.titleView];
    if (isHovered) {
      classes.push(styles.titleViewHidden);
      classes.push(styles.fadeOut);
    } else {
      classes.push(styles.fadeIn);
    }
    return classes.join(' ');
  }, [isHovered])

  const handleCloseModal = useCallback(async() => {
    setShowUnit(false)
    setShowLesson(false)
    setShowContentId(undefined)
    const unitPage = document.getElementById("unitsPage")
    if (unitPage) {
      unitPage.style.paddingBottom = '0';
    }
    if (refresh && !isAussie) {
      refresh()
    } else {
      try {
        setRefreshing(true);
        await getAusCourseContents()
      } catch(err) {
        logger.error(err)
      } finally {
        setRefreshing(false)
      }
    }
  }, [refresh, isAussie, getAusCourseContents]);

  const handleHoverUnit = useCallback((unitId: string, hovered: boolean) => {
    const unitElement = document.getElementById(`${program.id}_${unitId}`);
    const body = document.getElementById("root")
    const unitPage = document.getElementById("unitsPage")
    if (unitPage) {
      if (hovered) {
        unitPage.style.paddingBottom = '700px';
      } else {
        unitPage.style.paddingBottom = '0';
      }
    }
    if (unitElement && body) {
      let dims = unitElement.getBoundingClientRect();

      var elementPosition = dims.top;
      var offsetPosition = elementPosition + body.scrollTop - 70;

      body.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
    if (handleCardHovered) {
      setTimeout(() => {
        handleCardHovered(unitId, hovered)
      }, 100)
    }
  }, [handleCardHovered, program]);

  const handleSelectProgram = useCallback(() => {
    handleSeleted?.(program)
  }, [handleSeleted, program])

  return (
    <div>
      <div className={styles.backdrop} onClick={handleNoneSelect}/>
      <div className={styles.card} onMouseOver={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onClick={handleSelectProgram}>
        <div className={styles.image}
          style={{ backgroundImage: `url(${image ? getMediaAbsolutePath(image) : ''})` }}>
        </div>
        {program.coverVideo && <div className={styles.hoverVideo} style={{display: isHovered ? "block" : "none"}}>
          <VideoPlayer src={program?.coverVideo} isCoverVideo className={styles.video} autoPlay={isHovered} startTime={isHovered ? 0 : undefined} metaData={{video_title: 'Cover Video'}} controls={false} muted loop stopPlay={!isHovered} />
        </div>}
        {<div>
          <div className={hoverTitleViewStyle}>
            <div className={styles.hoverTitleSubView}>
              <div className={styles.hoverMainTitle}>{ isSelected ? "Close" : "View"}&nbsp;</div>
              <div className={styles.hoverBoldTitle}>{title}&nbsp;</div>
              <div className={styles.hoverMainTitle}>{subtitle}</div>
            </div>  
          </div>
          <div className={titleViewStyle}>
            <div className={styles.mainTitle}>{title}</div>
            <div className={styles.subTitle}>{subtitle}</div>
            { !!progress && progress > 0 && <div className={styles.progressBackBar}>
              <div className={styles.progressBar} style={{width: `${progress}%`, borderRadius: progress === 100 ? '3px' : ''}}></div>
              <div className={styles.iconView}>{cpres.map((item: any, index: number) => {
                const completed = Math.round((index + 1) / cpres.length * 100) <= Math.round(progress);
                return (
                  <div key={item.id} className={styles.lessonProgress}>
                    { completed ? <CompleteIcon /> : <BookMarkIcon className={styles.bookmarIcon} /> }
                  </div>
                )
              })}</div>
            </div>}
          </div>
        </div>}
      </div>
      <div id={program.id} className={styles.subView}>
        {(loading || loading1) && !refreshing ? <div className={styles.loadingWrapper}><Spinner /></div> :
        <Collapse isOpened={isSelected}>
          <DndProvider backend={HTML5Backend}>
          {isAussie && ausCourseContents.length > 0 ? 
          <div className={styles.content}>
            <div className={styles.unitView} style={{maxWidth: isAussie ? '1300px' : ''}}>
              <div className={styles.unitsDivWithFour}>
              {ausCourseContents.map((content, index) => {
                return (
                  <CoverContent
                    key={index}
                    type={"type"}
                    content={content}
                    handleOpenContent={handleOpenContent}
                  />
                )
              })}
              </div>
            </div>
          </div> :
          <div className={styles.content}>
            {coreUnits.length > 0 && <div className={styles.unitView}>
              <div className={styles.unitGroupTitle}></div>
              <div className={styles.unitsDiv}>
              {coreUnits.map((unit, index) => {
                return (
                  <UnitCard
                    key={index}
                    unit={unit}
                    handleEditUnit={handleEditUnit}
                    handleOpenUnit={handleOpenUnit}
                    handleCardHovered={handleHoverUnit}
                    cardHovered={cardHovered}
                    hoveredUnitId={hoveredUnitId}
                    index={index+1}
                    isSelected={isSelected}
                    handleSelectedCenterCard={handleSelectedCenterCard}
                    programID={program.id}
                  />
                )
              })}
              </div>
            </div>}
            {additionUnits.length > 0 && <div className={styles.unitView}>
              <div className={styles.unitGroupTitle}>Additional Content</div>
              <div className={styles.unitsDiv}>
              {additionUnits.map((unit, index) => {
                return (
                  <UnitCard
                    key={index}
                    unit={unit}
                    handleEditUnit={handleEditUnit}
                    handleOpenUnit={handleOpenUnit}
                    handleCardHovered={handleHoverUnit}
                    cardHovered={cardHovered}
                    hoveredUnitId={hoveredUnitId}
                    index={index+1}
                    isSelected={isSelected}
                    handleSelectedCenterCard={handleSelectedCenterCard}
                    programID={program.id}
                  />
                  )
                })}
              </div>
            </div>}
          </div>}
          </DndProvider>
        </Collapse>
        }
      </div>
      {selectedUnit?.id && (
        showUnit && <UnitProgressModal open={showUnit} onClose={handleCloseModal} unitId={selectedUnit?.id} program={program} unitData={selectedUnit} isDashboard isParentApp={isParentApp} parentSkill={parentSkill} />
      )}
      {showContentId && showUnit ? <UnitProgressModal open={showUnit} onClose={handleCloseModal} unitId={showContentId} program={program} isDashboard isParentApp={isParentApp} /> : null}
      {showContentId && showLesson ? <LessonProgressModal open={showLesson} onClose={handleCloseModal} lessonId={showContentId} programName={program.name} /> : null}
    </div>
  );
}

export default Card;
